/**
 * FIX 4.6 + Stage 7 — shell header cart control (multi-cart indicator + switcher entry).
 * Icon-only trigger — HubEmptyCartSheet / MultiCartSwitcherSheet owned by CustomerShellHeader.
 */

import { useTranslation } from 'react-i18next';
import { Icon, ShoppingCart } from 'pi-kiosk-shared/ui/Icon';
import { CUSTOMER_SHELL_ICON_BUTTON_CLASS } from './customerTopBarStyles.js';
import {
  useMultiCartRegistryRows,
  useNonemptyTenantCartCount,
} from './useMultiCartRegistry.js';
import {
  formatCartBadgeQuantity,
  useLastKnownShopCartQuantity,
} from './useLastKnownShopCartQuantity.js';
import { resolveTenantCartColor } from '../features/shop/cart/tenantCartColor.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from './platformChromeTenant.js';

export interface CustomerShellCartControlProps {
  readonly hubEmptyCartOpen: boolean;
  readonly multiCartSwitcherOpen: boolean;
  /** Toggles hub-empty / switcher / open-cart intent (re-click while open dismisses). */
  readonly onOpen: () => void;
  /**
   * HUX-01 ONE rule on root `/browse`: hide when empty; show when non-empty.
   * While a cart panel is open, keep the control mounted for dismiss parity.
   */
  readonly hideWhenEmpty?: boolean;
}

function resolveSingleCartItemQuantity(
  activeCartQuantity: number,
  soleRegistryQuantity: number,
): number {
  if (activeCartQuantity > 0) {
    return activeCartQuantity;
  }
  return soleRegistryQuantity;
}

function resolveOpenAriaLabel(
  t: (key: string, opts?: { count: number }) => string,
  tenantCartCount: number,
  activeCartQuantity: number,
  soleRegistryQuantity: number,
): string {
  if (tenantCartCount > 1) {
    return t('shop.cart.openAriaMulti', { count: tenantCartCount });
  }
  const itemQuantity = resolveSingleCartItemQuantity(
    activeCartQuantity,
    soleRegistryQuantity,
  );
  if (itemQuantity > 0) {
    return t('shop.cart.openAriaWithCount', { count: itemQuantity });
  }
  return t('shop.cart.openAria');
}

function resolveBadgeLabel(
  tenantCartCount: number,
  activeCartQuantity: number,
  soleRegistryQuantity: number,
): string {
  if (tenantCartCount > 1) {
    return formatCartBadgeQuantity(tenantCartCount);
  }
  return formatCartBadgeQuantity(
    resolveSingleCartItemQuantity(activeCartQuantity, soleRegistryQuantity),
  );
}

/**
 * Header cart — badge when any persisted cart has items.
 * Multi-tenant: badge shows tenant-cart count; single cart shows item count.
 * Root Home may pass `hideWhenEmpty` (HUX-01 cart ONE rule).
 */
export function CustomerShellCartControl({
  hubEmptyCartOpen,
  multiCartSwitcherOpen,
  onOpen,
  hideWhenEmpty = false,
}: CustomerShellCartControlProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const urlTenantCode = useTenantCode();
  const activeCartQuantity = useLastKnownShopCartQuantity();
  const tenantCartCount = useNonemptyTenantCartCount();
  const registryRows = useMultiCartRegistryRows();
  const soleRegistryQuantity =
    registryRows.length === 1 ? registryRows[0]?.totalQuantity ?? 0 : 0;
  const showBadge =
    tenantCartCount > 0 ||
    activeCartQuantity > 0 ||
    soleRegistryQuantity > 0;
  const badgeLabel = resolveBadgeLabel(
    tenantCartCount,
    activeCartQuantity,
    soleRegistryQuantity,
  );
  const openAria = resolveOpenAriaLabel(
    t,
    tenantCartCount,
    activeCartQuantity,
    soleRegistryQuantity,
  );
  const accentTenantCode = isPlatformChromeTenant(urlTenantCode)
    ? null
    : urlTenantCode;
  const accentColor =
    accentTenantCode !== null ? resolveTenantCartColor(accentTenantCode) : null;
  const panelOpen = hubEmptyCartOpen || multiCartSwitcherOpen;

  // HUX-01 ONE rule: hide empty cart on Home; keep mounted while a panel is open.
  if (hideWhenEmpty && !showBadge && !panelOpen) {
    return null;
  }

  return (
    <button
      type="button"
      className={CUSTOMER_SHELL_ICON_BUTTON_CLASS}
      aria-label={openAria}
      aria-expanded={panelOpen}
      aria-haspopup="dialog"
      title={openAria}
      data-testid="customer-shell-cart"
      onClick={onOpen}
    >
      <span className="relative inline-flex h-5 w-5 items-center justify-center">
        <Icon icon={ShoppingCart} size={20} aria-hidden={true} />
        {showBadge ? (
          <span
            className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white"
            style={
              accentColor !== null
                ? { backgroundColor: accentColor }
                : { backgroundColor: 'var(--color-danger)' }
            }
            aria-hidden
            data-testid="customer-shell-cart-badge"
          >
            {badgeLabel}
          </span>
        ) : null}
      </span>
    </button>
  );
}
